// src/pages/markets/components/TradesTable.tsx
//
// Generic, context-agnostic trade list. Extracted from
// TraderInsights.TraderTradeDetail so the same component renders a trader's
// fills inside both PositionsTable (one market/side) and TraderInsights
// (one account, all markets). Consumes the shared MarketTrade shape.

import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import type { MarketTrade } from '../api/useMarketTrades';

export interface TradesTableProps {
    trades: MarketTrade[];
    // Optional accent (e.g. the trader's assigned color); falls back to divider.
    accentColor?: string;
    // Cap the scroll area; defaults to 240px.
    maxHeight?: number;
    // Optional empty-state copy.
    emptyLabel?: string;
    // The side of the position being viewed. When set, an extra column shows
    // whether each fill grew or shrank that position; when omitted (e.g. the
    // TraderInsights all-markets view has no single "position"), the column
    // is left out entirely.
    positionSide?: 'YES' | 'NO';
}

function fmtDateTime(d: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fmtUsd(n: number): string {
    if (!Number.isFinite(n)) return '—';
    const abs = Math.abs(n);
    if (abs >= 1000) return `$${(n / 1000).toFixed(1)}k`;
    return `$${n.toFixed(2)}`;
}

function fmtPct(price: number): string {
    if (!Number.isFinite(price)) return '—';
    return `${(price * 100).toFixed(1)}%`;
}

function outcomeOf(idx: number | null): 'YES' | 'NO' | null {
    if (idx === 0) return 'YES';
    if (idx === 1) return 'NO';
    return null;
}

// Buying YES or selling NO both add YES-side exposure. Used only for the
// Position Δ column — the Side/Token pills show the raw fill facts, not this
// derived direction.
function isBullishOnYes(t: MarketTrade): boolean | null {
    const outcome = outcomeOf(t.outcomeIndex);
    if (outcome == null) return null;
    return (
        (t.side === 'buy' && outcome === 'YES') ||
        (t.side === 'sell' && outcome === 'NO')
    );
}

// Same visual language as SideBadge in PositionsTable.tsx — colored text on a
// light alpha chip — so pills read consistently across both tables.
function Pill({ label, positive }: { label: string; positive: boolean }) {
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-block',
                px: 0.6,
                py: 0.1,
                borderRadius: 0.75,
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.03em',
                color: positive ? 'success.main' : 'error.main',
                bgcolor: theme =>
                    alpha(
                        positive
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                        0.12
                    ),
            }}
        >
            {label}
        </Box>
    );
}

export function TradesTable({
    trades,
    accentColor,
    maxHeight = 240,
    emptyLabel = 'No trades for this position.',
    positionSide,
}: TradesTableProps) {
    const theme = useTheme();
    const accent = accentColor ?? theme.palette.divider;

    const { buyCount, sellCount } = useMemo(
        () => ({
            buyCount: trades.filter(t => t.side === 'buy').length,
            sellCount: trades.filter(t => t.side === 'sell').length,
        }),
        [trades]
    );

    if (trades.length === 0) {
        return (
            <Box sx={{ p: 1.5 }}>
                <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>
                    {emptyLabel}
                </Typography>
            </Box>
        );
    }

    // Newest first.
    const sorted = [...trades].sort(
        (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
    );

    const headers: readonly string[] = positionSide
        ? ['Time', 'Side', 'Token', 'Position Δ', 'Price', 'Shares', 'Value']
        : ['Time', 'Side', 'Token', 'Price', 'Shares', 'Value'];

    return (
        <Box sx={{ py: 0.5, px: 0.75 }}>
            <Box
                sx={{
                    display: 'flex',
                    gap: 1.5,
                    mb: 0.5,
                    pl: 0.25,
                }}
            >
                <Typography sx={{ fontSize: '9px', color: 'text.secondary' }}>
                    {trades.length} fills
                </Typography>
                <Typography sx={{ fontSize: '9px', color: 'success.main' }}>
                    {buyCount} buy
                </Typography>
                <Typography sx={{ fontSize: '9px', color: 'error.main' }}>
                    {sellCount} sell
                </Typography>
            </Box>

            <Box
                sx={{
                    maxHeight,
                    overflowY: 'auto',
                    borderLeft: `2px solid ${accent}`,
                    bgcolor: alpha(accent, 0.03),
                    borderRadius: '0 4px 4px 0',
                }}
            >
                <Box
                    component="table"
                    sx={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '11px',
                    }}
                >
                    <Box
                        component="thead"
                        sx={{
                            position: 'sticky',
                            top: 0,
                            bgcolor: 'background.paper',
                            zIndex: 1,
                        }}
                    >
                        <Box component="tr">
                            {headers.map((h, i) => (
                                <Box
                                    key={h}
                                    component="th"
                                    title={
                                        h === 'Position Δ'
                                            ? 'Size and token direction this fill added to your position'
                                            : undefined
                                    }
                                    sx={{
                                        textAlign: i === 0 ? 'left' : 'right',
                                        fontSize: '9px',
                                        fontWeight: 600,
                                        letterSpacing: '0.04em',
                                        textTransform: 'uppercase',
                                        color: 'text.disabled',
                                        py: 0.4,
                                        px: 0.75,
                                    }}
                                >
                                    {h}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    <Box component="tbody">
                        {sorted.map(t => {
                            const isBuy = t.side === 'buy';
                            const outcome = outcomeOf(t.outcomeIndex);
                            const bullish = isBullishOnYes(t);

                            let deltaToken: 'YES' | 'NO' | null = null;
                            if (positionSide) {
                                if (bullish === true) deltaToken = 'YES';
                                else if (bullish === false) deltaToken = 'NO';
                            }

                            return (
                                <Box
                                    key={t.id}
                                    component="tr"
                                    sx={{
                                        '&:hover': {
                                            bgcolor: alpha(
                                                theme.palette.action.hover,
                                                0.06
                                            ),
                                        },
                                    }}
                                >
                                    <Box
                                        component="td"
                                        sx={{
                                            py: 0.4,
                                            px: 0.75,
                                            fontFamily: 'monospace',
                                            color: 'text.secondary',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {fmtDateTime(t.timestamp)}
                                    </Box>
                                    <Box
                                        component="td"
                                        sx={{
                                            py: 0.4,
                                            px: 0.75,
                                            textAlign: 'right',
                                        }}
                                    >
                                        <Pill
                                            label={isBuy ? 'BUY' : 'SELL'}
                                            positive={isBuy}
                                        />
                                    </Box>
                                    <Box
                                        component="td"
                                        sx={{
                                            py: 0.4,
                                            px: 0.75,
                                            textAlign: 'right',
                                        }}
                                    >
                                        {outcome ? (
                                            <Pill
                                                label={outcome}
                                                positive={outcome === 'YES'}
                                            />
                                        ) : (
                                            <Typography
                                                sx={{
                                                    fontSize: '11px',
                                                    color: 'text.disabled',
                                                }}
                                            >
                                                —
                                            </Typography>
                                        )}
                                    </Box>
                                    {positionSide && (
                                        <Box
                                            component="td"
                                            sx={{
                                                py: 0.4,
                                                px: 0.75,
                                                textAlign: 'right',
                                                fontFamily: 'monospace',
                                                fontWeight: 600,
                                                whiteSpace: 'nowrap',
                                                color:
                                                    deltaToken == null
                                                        ? 'text.disabled'
                                                        : deltaToken === 'YES'
                                                          ? 'success.main'
                                                          : 'error.main',
                                            }}
                                        >
                                            {deltaToken == null
                                                ? '—'
                                                : `+${t.amount.toFixed(1)} ${deltaToken}`}
                                        </Box>
                                    )}
                                    <Box
                                        component="td"
                                        sx={{
                                            py: 0.4,
                                            px: 0.75,
                                            textAlign: 'right',
                                            fontFamily: 'monospace',
                                        }}
                                    >
                                        {fmtPct(t.price)}
                                    </Box>
                                    <Box
                                        component="td"
                                        sx={{
                                            py: 0.4,
                                            px: 0.75,
                                            textAlign: 'right',
                                            fontFamily: 'monospace',
                                            color: 'text.secondary',
                                        }}
                                    >
                                        {t.amount.toFixed(1)}
                                    </Box>
                                    <Box
                                        component="td"
                                        sx={{
                                            py: 0.4,
                                            px: 0.75,
                                            textAlign: 'right',
                                            fontFamily: 'monospace',
                                        }}
                                    >
                                        {fmtUsd(t.quoteAmount)}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
